import Papa from 'papaparse'
import moment from 'moment-timezone'
import Base from '@/resources/Base'
import {dateTimeSort} from '@/utilities/datetime.helper'
import {arrayToJSONString} from '@/utilities/filters/array-to-jsonstring'
import {specialCharacters} from '@/utilities/regex/special-characters'
import {handleNulls} from '@/utilities/sort.helper'
import {OrderByEnum} from '@/constants/orderby.constants'
import {NANOSECONDS_TO_MILLISECONDS} from '@/constants/datetime.constants'

export default class IQCollection extends Base {
  /**
   * Collection for model classes.
   *
   * @param {*} models  Models to add to this collection.
   * @param {*} options Options to set on this collection.
   * @param {*} attributes Attributes used for search on this collection.
   *
   **/
  constructor (models = [], attributes = {}, options = {}) {
    super('', attributes, options)

    this.currentPage = 1 // page number of currently active page
    this.totalRecords = models.length // length of total records
    this.recordsPerPage = this.options.recordsPerPage || 100
    this.indexData = {
      // start and end index of records
      startIndex: 0, // on current page
      endIndex: 0
    }
    this.reloadToolbar = true
    this.key = true

    /**
     * handles state of vue-infinite-loading
     * 'loading' = more data to be loaded
     * 'complete' = all data has been loaded
     * 'reset' = init state
     */
    this.virtualState = undefined

    this.filteredSearch = false
    this.searchTags = [] // tags
    this.searchNameKeys = [] // tags
    this.filteredSearchObjects = [] // objects used for filterDataByProperty

    this.models = [] // cache of original dataset
    this.queriedData = [] // recordset of returned queried recordset

    this.setSearchPropertyNameKey(this.attributes)
    this.copySearchCache(models) // original recordset

    // Default configs for csv output
    this.csvConfig = {
      name: this.options.csvName,
      quotes: false,
      quoteChar: '"',
      escapeChar: '"',
      delimiter: ',',
      header: true,
      newline: '\r\n',
      skipEmptyLines: 'greedy', // If set to 'greedy', lines that don't have any content (those which have only whitespace after parsing) will also be skipped.
      columns: _.map(this.attributes, 'name')
    }

    this.csvConfig.columns = this.csvConfig.columns.map(csvColName => {
      if (csvColName.includes('.')) {
        return csvColName.split('.')[0]
      } else if (csvColName.includes('[')) {
        return csvColName.split('[')[0]
      }
      return csvColName
    })

    this.defaultSort()
  }

  getCount () {
    return this.models.length
  }

  getQueriedCount () {
    return this.queriedData.length
  }

  hasMoreRecords (page) {
    let activeArr = this.searchTags.length || this.filteredSearch ? this.queriedData : this.models
    let i = (page + 1) * this.recordsPerPage
    if (i <= activeArr.length || (page * this.recordsPerPage < activeArr.length && activeArr.length <= i)) {
      return true
    }
    return false
  }

  getNextPage (page) {
    let activeArr = this.searchTags.length || this.filteredSearch ? this.queriedData : this.models
    let i = page * this.recordsPerPage
    if (i < activeArr.length) {
      return activeArr.slice(i, (page + 1) * this.recordsPerPage)
    }
    return activeArr.slice(i, activeArr.length)
  }

  /**
   *
   * Method to filter out records for currently
   * active page based on 'page number'
   * and 'records per page'
   *
   **/
  getRecords () {
    let activeArr = this.searchTags.length || this.filteredSearch ? this.queriedData : this.models
    this.totalRecords = activeArr.length

    let i = (this.currentPage - 1) * this.recordsPerPage
    let recordsArray = activeArr.slice(i, this.currentPage * this.recordsPerPage)
    let end =
      recordsArray.length % this.recordsPerPage === 0
        ? this.currentPage * this.recordsPerPage
        : (this.currentPage - 1) * this.recordsPerPage + recordsArray.length

    let start = i + 1
    if (!recordsArray.length) {
      start = 0
      end = 0
    }

    return {
      records: recordsArray,
      startIndex: start,
      endIndex: end
    }
  }

  /**
   *
   * Method to set start and end index of records on
   * currently active page
   *
   **/
  setIndexData (start, end) {
    this.indexData.startIndex = start
    this.indexData.endIndex = end
  }

  /**
   *
   * Method to get start and end index of records on
   * currently active page
   *
   **/
  getIndexData () {
    return this.indexData
  }

  /**
   *
   * Method to sort collection records by given property
   * (Using element io sortby)
   *
   */
  defaultSort () {
    if (_.isEmpty(this.options.defaultSort)) {
      return false
    }
    let attrSort = _.find(this.attributes, { name: this.options.defaultSort })
    if (attrSort.hasOwnProperty('datatype') && attrSort.hasOwnProperty('name')) {
      this.sortBy(attrSort.datatype, attrSort.name, this.options.sortOrder)
    }
  }

  /**
   *
   * Method to sort collection records by given property
   * (Using element io sortby)
   *
   */
  sortBy (dataType, sortKey, reverse) {
    let sortedArr = this.searchTags.length || this.filteredSearch ? this.queriedData : this.models

    if (_.findIndex(sortedArr, el => _.has(el, 'children')) !== -1) {
      sortedArr.forEach((item, idx) => {
        if (_.has(item, 'children')) {
          item.children.sort((a, b) => {
            return this.applySort(a, b, dataType, sortKey, reverse)
          })
        }
      })
    } else {
      return sortedArr.sort((a, b) => {
        return this.applySort(a, b, dataType, sortKey, reverse)
      })
    }
  }

  applySort (a, b, dataType, sortKey, reverse) {
    let sortFn = {}
    // sort method based on column type
    sortFn = this.getSortFn(dataType)
    if (reverse === OrderByEnum.DESC) {
      if (dataType === 'objectpath') {
        return sortFn.call(this, _.get(b, sortKey), _.get(a, sortKey))
      }
      return sortFn.call(this, b[sortKey], a[sortKey])
    }
    if (dataType === 'objectpath') {
      return sortFn.call(this, _.get(a, sortKey), _.get(b, sortKey))
    }
    return sortFn.call(this, a[sortKey], b[sortKey])
  }

  /**
   *
   * Get sort function by data type
   *
   */
  getSortFn (itemType) {
    switch (itemType.toLowerCase()) {
      case 'number':
        return this.sortNumber
      case 'boolean':
        return this.sortBool
      case 'string':
        return this.sortAlphaNum
      case 'dwelltime_string':
        return this.dwellTimeSort
      case 'runtime_string':
        return this.runTimeSort
      case 'datetime':
        return dateTimeSort
      case 'ip_address':
        return this.sortIp
      default:
        return this.basicSort // datatime comes in as timestamp since epoch
    }
  }

  /**
   *
   * number sort
   *
   */
  sortNumber (a, b) {
    let nulls = this.handleNulls(a, b)
    if (nulls !== null) {
      return nulls
    }
    return a - b
  }

  /**
   * boolean sort
   */
  sortBool (a, b) {
    let nulls = this.handleNulls(a, b)
    if (nulls !== null) {
      return nulls
    } else if ((a && b) || (!a && !b)) {
      return 0
    }
    return a ? 1 : -1
  }

  /**
   * alphanumeric sort
   */
  sortAlphaNum (a, b) {
    let nulls = this.handleNulls(a, b)
    if (nulls !== null) {
      return nulls
    }
    let strA = a.toString().toLowerCase()
    let strB = b.toString().toLowerCase()
    return strA === strB ? 0 : strA.localeCompare(strB)
  }

  /**
   * runTime sort
   */
  runTimeSort (a, b) {
    if (a === 'N/A') return -1
    if (b === 'N/A') return 1

    let [valA, unitA] = [...a.split(' ')]
    let [valB, unitB] = [...b.split(' ')]

    unitA = unitA === 'months' ? 'M' : unitA[0]
    unitB = unitB === 'months' ? 'M' : unitB[0]

    let result = this.compareUnits(unitA, unitB)
    if (result === 0) {
      if (parseInt(valA) === parseInt(valB)) {
        return 0
      } else if (parseInt(valA) > parseInt(valB)) {
        return 1
      } else {
        return -1
      }
    }
    return result
  }

  /**
   * dwellTime sort
   */
  dwellTimeSort (a, b) {
    let arrA = a.split(' ')
    let arrB = b.split(' ')
    for (let i = 0; i < arrA.length && i < arrB.length; i++) {
      let result = this.compareTime(arrA[i], arrB[i])
      if (result != 0) {
        return result
      }
    }
    if (arrA.length === arrB.length) return 0
    else if (arrA.length > arrB.length) return 1
    else return -1
  }

  compareTime (a, b) {
    let valA = a.slice(0, -1)
    let unitA = a.slice(-1)

    let valB = b.slice(0, -1)
    let unitB = b.slice(-1)

    let result = this.compareUnits(unitA, unitB)
    if (result === 0) {
      if (parseInt(valA) === parseInt(valB)) {
        return 0
      } else if (parseInt(valA) > parseInt(valB)) {
        return 1
      } else {
        return -1
      }
    }
    return result
  }

  compareUnits (a, b) {
    let units = ['y', 'M', 'd', 'h', 'm', 's']
    let idxA = units.indexOf(a)
    let idxB = units.indexOf(b)
    if (idxA === idxB) return 0
    else if (idxA < idxB) return 1
    else return -1
  }

  /**
   * basic sort
   */
  basicSort (a, b) {
    let nulls = this.handleNulls(a, b)
    if (nulls !== null) {
      return nulls
    }
    if (a === b) {
      return 0
    }
    if (a < b) {
      return -1
    }
    return 1
  }

  /**
   *
   * IP address sort
   *
   */
   sortIp (a, b) {
    let nulls = this.handleNulls(a, b)
    if (nulls !== null) {
      return nulls
    }
    // arrayOfIps.sort((a, b) => {
    const num1 = Number(a.split(".").map((num) => (`000${num}`).slice(-3) ).join(""));
    const num2 = Number(b.split(".").map((num) => (`000${num}`).slice(-3) ).join(""));
    return num1-num2;
    // });
  }

  /**
   *
   * Method to retrieve property 'name' of search properties.
   * Assuming this property is used for display.
   *
   */
  setSearchPropertyNameKey (searchProperties) {
    if (_.isUndefined(searchProperties) || searchProperties.length === 0) {
      this.searchNameKeys.length = 0
    } else {
      this.searchNameKeys = _.map(searchProperties, 'name')
    }
  }

  /**
   * JSON Stringify record based on search property keys.
   *
   * @param {*} jsonData
   * @param {*} searchPropertyArray
   */
  convertToJSONString (jsonData, searchPropertyArray) {
    let jsonString = ''
    let specialCharRegex = new RegExp(specialCharacters, 'g')
    for (let key in jsonData) {
      let attrValue = jsonData[key]
      let propertyKeys = []
      if (_.isNull(attrValue)) {
        continue
      } else if (typeof attrValue === 'object') {
        // for objects, handle multiple keys due to objectpaths
        // find all and stringify each property
        propertyKeys = _.filter(searchPropertyArray, function (field) {
          return field.name.startsWith(key)
        })
        if (_.isEmpty(propertyKeys) && _.isArray(attrValue)) {
          propertyKeys = _.filter(searchPropertyArray, { name: key })
        }
      } else {
        propertyKeys = _.filter(searchPropertyArray, { name: key })
      }

      // check if field is visible key to be searched on
      if (_.isEmpty(propertyKeys)) {
        continue // move on to next property
      }

      propertyKeys.forEach(propertyKey => {
        if (_.isArray(attrValue)) {
          // In case of array of objects, object is stringified first.
          if (propertyKey.datatype === 'objectpath') {
            attrValue = _.get(jsonData, propertyKey.name)
          } else {
            for (let index = 0; index < attrValue.length; index++) {
              attrValue[index] = arrayToJSONString(attrValue[index])
            }
            // for array types, join each item with '|'
            attrValue = attrValue.join('|')
          }
          // if property is not defined or empty array, set as ''
          if (_.isEmpty(attrValue) || _.isUndefined(attrValue)) {
            attrValue = ''
          }
          // globally replace all special characters
          let searchObject = attrValue.replace(specialCharRegex, '')
          // search will be case insensitive
          jsonString += String(searchObject).toLowerCase() + ','
        } else {
          if (propertyKey.datatype === 'datetime') {
            // convert startTime to ms if necessary
            if (attrValue && !moment(attrValue).isValid()) {
              attrValue = attrValue * NANOSECONDS_TO_MILLISECONDS
            }
            attrValue = moment(attrValue).format(propertyKey.format)
          } else if (propertyKey.datatype === 'objectpath') {
            if (propertyKey.display === 'component') {
              if (propertyKey.component === 'InstanceEntityTableCell' && propertyKey.name === 'entities') {
                attrValue = JSON.stringify(attrValue)
              } else {
                attrValue = _.get(jsonData, propertyKey.displayTextName)
              }
            } else if (propertyKey.display === 'mitrelink') {
              attrValue = _.get(jsonData, propertyKey.linkLabel.labelId)
              attrValue += ': ' + _.get(jsonData, propertyKey.linkLabel.labelName)
            } else {
              attrValue = _.get(jsonData, propertyKey.name)
            }
          } else if (propertyKey.displayKey && propertyKey.displayKey.length) {
            let rowValue = _.get(jsonData, propertyKey.name)
            let valFound = _.find(propertyKey.displayKey, { key: rowValue })
            if (valFound) {
              attrValue = valFound.display || valFound.key
            }
          }
          // search will be case insensitive
          jsonString += String(attrValue).toLowerCase() + ','
        }
      })
    }
    return jsonString
  }

  /**
   * Copy original dataset and map new field 'stringify' to stringified values of row/record.
   * Field stringify will be used to for search.
   *
   * @param {*} dataSet
   */
  copySearchCache (dataSet) {
    if (!dataSet) {
      return dataSet
    }

    this.models = []
    for (let idx in dataSet) {
      // deep copy record as we will mutate it
      let record = dataSet[idx]
      if (_.isUndefined(this.attributes) || this.attributes.length === 0) {
        record.stringify = JSON.stringify(record).toLowerCase()
      } else {
        record.stringify = this.convertToJSONString(record, this.attributes)
      }
      this.models.push(record)
    }
  }

  searchData (searchTags) {
    // if no search tags, return all data
    if (searchTags.length === 0) {
      this.searchTags.length = 0
      if (!_.isEmpty(this.filteredSearchObjects)) {
        this.queriedData.length = 0
        let cloneFilterSearchObjs = _.clone(this.filteredSearchObjects)
        this.filteredSearchObjects.length = 0

        cloneFilterSearchObjs.forEach(filterObj => {
          this.filterDataByProperty(filterObj.name, filterObj.value)
        })
        return this.queriedData
      } else {
        return _.cloneDeep(this.models)
      }
    }

    if (!_.isArray(searchTags)) {
      searchTags = [searchTags]
    }

    // if search tag is added, continue search an queriedData
    if (searchTags.length > 1 && searchTags.length > this.searchTags.length) {
      let newTags = _.difference(_.map(searchTags, 'text'), this.searchTags)
      let indexesToBeDeleted = []

      this.queriedData.forEach((row, index) => {
        // if row doesn't match search, remove
        newTags.forEach(tag => {
          if (!this.checkRowForSearchTag(tag, row, index)) {
            indexesToBeDeleted.push(index)
          }
        })
      })

      for (let idx = indexesToBeDeleted.length - 1; idx >= 0; idx--) {
        this.queriedData.splice(indexesToBeDeleted[idx], 1)
      }
    } else {
      if (!_.isEmpty(this.filteredSearchObjects)) {
        this.queriedData.length = 0
        let cloneFilterSearchObjs = _.clone(this.filteredSearchObjects)
        this.filteredSearchObjects.length = 0
        this.searchTags = _.map(searchTags, 'text')

        cloneFilterSearchObjs.forEach(filterObj => {
          this.filterDataByProperty(filterObj.name, filterObj.value)
        })

        let indexesToBeDeleted = []
        this.queriedData.forEach((row, index) => {
          // if row doesn't match search, remove
          _.map(searchTags, 'text').forEach(tag => {
            if (!this.checkRowForSearchTag(tag, row, index)) {
              indexesToBeDeleted.push(index)
            }
          })
        })

        for (let idx = indexesToBeDeleted.length - 1; idx >= 0; idx--) {
          this.queriedData.splice(indexesToBeDeleted[idx], 1)
        }
      } else {
        // second case here
        // 1. first tag is added
        // 2. then tag removed, in which case we should empty queried data and search on models
        this.queriedData.length = 0

        this.models.forEach((row, index) => {
          _.map(searchTags, 'text').forEach(tag => {
            if (this.checkRowForSearchTag(tag, row, index)) {
              this.queriedData.push(row)
            }
          })
        })
      }
    }
    this.searchTags = _.map(searchTags, 'text')
    this.totalRecords = this.queriedData.length
    return this.queriedData
  }

  /**
   * Search record for tag (ignorecase)
   * @param {*} searchTag
   * @param {*} row
   * @param {*} index
   *
   */
  checkRowForSearchTag (searchTag, row, index) {
    return row.stringify.includes(searchTag.toLowerCase())
  }

  /**
   * Search dataset by individual properties
   *
   * @param {*} searchTags
   * @param {*} searchField
   */
  searchDataByProperty (searchTags, searchField) {
    let match = false
    // global regex
    let specialCharRegex = new RegExp(specialCharacters, 'g')
    this.queriedData.length = 0

    if (!_.isArray(searchTags)) {
      searchTags = [searchTags]
    }

    this.models.forEach(
      function (row, index) {
        match = false
        if (!_.isUndefined(row[searchField]) && !_.isUndefined(searchTags)) {
          let value
          if (_.isArray(row[searchField])) {
            let arrayValue = row[searchField].toString().replace(specialCharRegex, '')
            value = arrayValue.toString().toLowerCase()
          } else {
            value = row[searchField].toString().toLowerCase()
          }
          searchTags.forEach(function (searchTag) {
            if (value === searchTag.toLowerCase()) {
              match = true
            }
          })
        }

        if (!match) {
          // do nothing on not found
        } else {
          this.queriedData.push(row)
        }
      }.bind(this)
    )
    this.searchTags = _.map(searchTags, 'text')
    this.totalRecords = this.queriedData.length
    return this.queriedData
  }

  searchTagsDataset (dataset) {
    this.searchTags.forEach(tag => {
      let indexesToBeDeleted = []
      dataset.forEach((row, index) => {
        // if row doesn't match search, remove
        if (!this.checkRowForSearchTag(tag, row, index)) {
          indexesToBeDeleted.push(index)
        }
      })
      for (let idx = indexesToBeDeleted.length - 1; idx >= 0; idx--) {
        dataset.splice(indexesToBeDeleted[idx], 1)
      }
    })
  }

  /**
   * Filter dataset by individual properties.
   * 1. Searchtag will not be set.
   * 2. Filter on queriedData data
   *
   * @param {*} searchTags
   * @param {*} searchField
   */
  filterDataByProperty (property, value) {
    this.filteredSearch = true
    // let match = false
    let modelForSearch = []

    // if search on existing object:value, return same result
    if (_.some(this.filteredSearchObjects, { name: property, value: value })) {
      return this.queriedData
    }

    if (_.isEmpty(this.filteredSearchObjects)) {
      modelForSearch = _.cloneDeep(this.models)
    } else {
      modelForSearch = this.queriedData
    }

    this.filteredSearchObjects.push({
      name: property,
      value: value
    })

    // global regex
    let specialCharRegex = new RegExp(specialCharacters, 'g')

    let indexesToBeDeleted = []
    modelForSearch.forEach(function (row, index) {
      // match = false
      if (!_.isUndefined(row[property]) && !_.isUndefined(value)) {
        let searchValue
        if (_.isArray(row[property])) {
          let arrayValue = row[property].toString().replace(specialCharRegex, '')
          searchValue = arrayValue.toString().toLowerCase()
        } else {
          searchValue = row[property].toString().toLowerCase()
        }
        // set match if value === searchValue
        if (searchValue !== value.toLowerCase()) {
          // match = true
          indexesToBeDeleted.push(index)
        }
      }

      // if (!match) {
      //   // do nothing on not found
      // } else {
      //   this.queriedData.push(row)
      // }
    })

    for (let idx = indexesToBeDeleted.length - 1; idx >= 0; idx--) {
      modelForSearch.splice(indexesToBeDeleted[idx], 1)
    }
    this.queriedData = modelForSearch
    if (!_.isEmpty(this.searchTags)) {
      this.searchTagsDataset(this.queriedData)
    }
    this.totalRecords = this.queriedData.length
    return this.queriedData
  }

  removeFilteredObject (property, value) {
    let found = _.findIndex(this.filteredSearchObjects, { name: property, value: value })
    if (found > -1) {
      this.filteredSearchObjects.splice(found, 1)
      this.removeFilteredObjectAndSearch(property, value)
    }
  }

  removeFilteredObjectAndSearch (property, value) {
    this.queriedData.length = 0
    this.removeFilteredObject(property, value)

    let cloneFilterSearchObjs = _.clone(this.filteredSearchObjects)
    this.filteredSearchObjects.length = 0

    cloneFilterSearchObjs.forEach(filterObj => {
      this.filterDataByProperty(filterObj.name, filterObj.value)
    })

    if (!_.isEmpty(this.searchTags)) {
      if (_.isEmpty(cloneFilterSearchObjs)) {
        this.queriedData = _.cloneDeep(this.models)
        this.filteredSearch = false
      }
      this.searchTagsDataset(this.queriedData)
    } else if (_.isEmpty(cloneFilterSearchObjs)) {
      this.queriedData = _.cloneDeep(this.models)
      this.filteredSearch = false
    }
    return this.queriedData
  }

  resetFilteredObjectAndSearch () {
    this.filteredSearch = false
    this.queriedData.length = 0
    this.filteredSearchObjects.length = 0
    if (!_.isEmpty(this.searchTags)) {
      let cloneTags = _.clone(this.searchTags)
      this.searchTags.length = 0
      const tagsArray = []
      cloneTags.forEach(tag => {
        tagsArray.push({ text: tag })
        this.searchData(tagsArray)
      })
    }
    return this.queriedData
  }

  handleNulls (a, b) {
    return handleNulls(a, b)
  }

  /**
   *
   * Method to modify visible columns
   * based on selected columns
   *
   */
  updateVisibleColumns (totalColumns, selectedColumns) {
    let visibleColumns = []
    totalColumns.forEach(column => {
      selectedColumns.forEach(visibleColumn => {
        if (column.label === visibleColumn) {
          visibleColumns.push(column)
        }
      })
    })
    return visibleColumns
  }

  downloadCsv (headers = undefined) {
    let exportedFilenmae = this.csvConfig.name ? `${this.csvConfig.name}.csv` : `${this.constructor.name}.csv`
    let generatedCsv = Papa.unparse(this.models, this.csvConfig)
    if (headers) {
      for (let header of headers) {
        if (header.columns) {
          for (let column of header.columns) {
            if (_.isArray(column.name)) {
              // skip for now
            } else {
              if (_.isEmpty(column.label)) {
                generatedCsv = generatedCsv.replace(column.name, column.description)
              } else {
                generatedCsv = generatedCsv.replace(column.name, column.label)
              }
            }
          }
        } else {
          generatedCsv = generatedCsv.replace(header.name, header.label)
        }
      }
    }

    // replace null with empty
    generatedCsv = generatedCsv.replaceAll('null', '')

    let blob = new Blob([generatedCsv], { type: 'text/csv;charset=utf-8;' })
    if (navigator.msSaveBlob) {
      // IE 10+
      navigator.msSaveBlob(blob, exportedFilenmae)
    } else {
      let link = document.createElement('a')
      if (link.download !== undefined) {
        // feature detection
        // Browsers that support HTML5 download attribute
        let url = URL.createObjectURL(blob)
        link.setAttribute('href', url)
        link.setAttribute('download', exportedFilenmae)
        link.style.visibility = 'hidden'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      }
    }
  }
}



// WEBPACK FOOTER //
// ./src/resources/collections/IQCollection.js